// src/components/QRDisplay.jsx
import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { fetchQRData } from "../utils/fetchQRData";
import { generateQRCodeSheet } from "../utils/pdfGenerator";

const CONFIG_URL =
  "https://amarraqrbucket.s3.us-east-1.amazonaws.com/qr-config.json";

function niceMonthLabel(monthKey) {
  try {
    const [y, m] = monthKey.split("-");
    const d = new Date(`${y}-${m}-01T00:00:00Z`);
    return d.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return monthKey;
  }
}

function isStillValid(monthData) {
  if (!monthData?.expires) return false;

  const [month, day, year] = monthData.expires.split("/").map(Number);

  // Keep the PDF visible through the entire expiration day.
  // Example: May 2026 expires 06/10/2026, so it disappears on 06/11/2026.
  const expirationDate = new Date(year, month - 1, day, 23, 59, 59);
  const today = new Date();

  return today <= expirationDate;
}

export default function QRDisplay({ monthKey }) {
  // all months JSON (for fallback + other-month downloads)
  const [allData, setAllData] = useState({});
  const [allErr, setAllErr] = useState("");

  // which month we are actually showing after fallback logic
  const [displayKey, setDisplayKey] = useState(monthKey);
  const [showFallback, setShowFallback] = useState(false);

  // QR payload for displayKey
  const [data, setData] = useState(null);

  // refs for current and hidden canvases
  const qrBoxRef = useRef(null);
  const canvasMapRef = useRef({});

  // Load all months (no cache) for deciding fallback + “other months”
  useEffect(() => {
    fetch(CONFIG_URL, { cache: "no-store" })
      .then((r) => r.json())
      .then((json) => {
        const store = json || {};
        setAllData(store);

        // Decide what to show:
        // 1) if requested month exists, use it
        // 2) else use the latest available month <= requested
        // 3) else use the latest available month overall
        const keys = Object.keys(store).sort(); // YYYY-MM sorts lexicographically
        if (keys.length === 0) {
          setDisplayKey(monthKey);
          setShowFallback(false);
          return;
        }

        if (store[monthKey]) {
          setDisplayKey(monthKey);
          setShowFallback(false);
        } else {
          const prevOrEqual = keys.filter((k) => k <= monthKey).pop();
          const chosen = prevOrEqual || keys[keys.length - 1];
          setDisplayKey(chosen);
          setShowFallback(true);
        }
      })
      .catch(() => setAllErr("Failed to load months."));
  }, [monthKey]);

  // Fetch data for the month we decided to display
  useEffect(() => {
    if (!displayKey) return;
    fetchQRData(displayKey)
      .then(setData)
      .catch((e) => {
        console.error("Failed to load QR config:", e);
        setData(null);
      });
  }, [displayKey]);

  if (!data) return <p>Loading QR data...</p>;

  const handleDownloadCurrent = () => {
    const canvas = qrBoxRef.current?.querySelector("canvas");
    if (!canvas) return;
    generateQRCodeSheet({
      canvas,
      month: displayKey,
      code: data.code,
      expires: data.expires,
      format: "letter",
    });
  };

  const handleDownloadMonth = (k) => {
    const holder = canvasMapRef.current[k];
    const canvas = holder?.querySelector("canvas");
    const m = allData[k];
    if (!canvas || !m?.link) return alert("QR not ready. Try again.");
    generateQRCodeSheet({
      canvas,
      month: k,
      code: m.code,
      expires: m.expires,
      format: "letter",
    });
  };

  // Exclude the displayed month and any expired months from the list
  const otherKeys = Object.keys(allData || {})
    .filter((k) => k !== displayKey)
    .filter((k) => isStillValid(allData[k]))
    .sort();

  return (
    <div className="qr-wrap">
      {/* Fallback banner */}
      {showFallback && (
        <div
          className="admin-alert admin-alert-error"
          style={{ maxWidth: 720 }}
        >
          Heads up: <b>{niceMonthLabel(monthKey)}</b> isn’t published yet.
          Showing <b>{niceMonthLabel(displayKey)}</b> instead.
        </div>
      )}

      {/* Current (or fallback) month QR inside the bordered box */}
      <div
        className="qr-box"
        ref={qrBoxRef}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 16,
          border: "6px solid #002060",
          borderRadius: 16,
          backgroundColor: "#fff",
          width: "max-content",
          margin: "0 auto",
        }}
      >
        {/* Month label inside the box */}
        <div
          style={{
            color: "#002060",
            fontSize: "1.25rem",
            fontWeight: "bold",
            marginBottom: 8,
          }}
        >
          {niceMonthLabel(displayKey)}
        </div>

        <QRCodeCanvas
          value={data.link}
          size={256}
          includeMargin={true}
          level="H"
          bgColor="#ffffff"
          fgColor="#002060"
        />

        <div
          className="qr-label"
          style={{ color: "#002060", marginTop: 8, fontSize: "0.95rem" }}
        >
          Scan me to open gate
        </div>
      </div>

      <p className="qr-meta" style={{ marginTop: 12 }}>
        Gate Code: <b>{data.code}</b>
        <br />
        Expires: {data.expires}
      </p>

      <a
        href={data.link}
        target="_blank"
        rel="noopener noreferrer"
        className="gate-link"
        style={{ marginBottom: 8 }}
      >
        Tap here to open gate
      </a>

      <button
        className="gate-link"
        onClick={handleDownloadCurrent}
        style={{ marginTop: 8 }}
      >
        Download {niceMonthLabel(displayKey)} PDF
      </button>

      {otherKeys.length > 0 && (
        <>
          <hr
            style={{
              margin: "24px 0",
              border: "none",
              borderTop: "1px solid #eee",
              width: "100%",
            }}
          />

          <div className="admin-card" style={{ maxWidth: 720 }}>
            <h2 className="admin-title" style={{ fontSize: "1.25rem" }}>
              Download PDFs for Other Months
            </h2>

            {allErr && (
              <div className="admin-alert admin-alert-error">{allErr}</div>
            )}

            <ul className="admin-list">
              {otherKeys.map((k) => {
                const m = allData[k];
                const label = niceMonthLabel(k);

                return (
                  <li key={k} className="admin-list-item">
                    <div className="admin-list-text">
                      <b>{label}</b>
                    </div>

                    <div className="admin-actions">
                      <button
                        className="btn"
                        onClick={() => handleDownloadMonth(k)}
                        disabled={!m?.link}
                      >
                        Download {label} PDF
                      </button>
                    </div>

                    {/* offscreen QR to render canvas for PDF generation */}
                    <div
                      ref={(el) => (canvasMapRef.current[k] = el)}
                      style={{ position: "absolute", left: -9999, top: -9999 }}
                      aria-hidden
                    >
                      {m?.link && (
                        <QRCodeCanvas
                          value={m.link}
                          size={256}
                          includeMargin={true}
                          level="H"
                          bgColor="#ffffff"
                          fgColor="#002060"
                        />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
